import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import type { Mode, Settings, Stats, Todo } from "../types";
import { todayKey } from "../utils/storage";

interface PomodoroTimerProps {
  settings: Settings;
  setStats: Dispatch<SetStateAction<Stats>>;
  activeTodo: Todo | null;
  bumpActiveTodoPomodoro: () => void;
  completedInRound: number;
  setCompletedInRound: Dispatch<SetStateAction<number>>;
  /** 跳到下方任务列表锚点 */
  onRequestPickTask?: () => void;
}

const MODE_LABEL: Record<Mode, string> = {
  focus: "专注",
  shortBreak: "短休息",
  longBreak: "长休息"
};

const MODE_THEME: Record<
  Mode,
  {
    bg: string;
    bgHover: string;
    text: string;
    chipActive: string;
    progress: string;
    progressSoft: string;
    label: string;
  }
> = {
  focus: {
    bg: "bg-focus",
    bgHover: "hover:bg-focus-hover",
    text: "text-focus",
    chipActive: "bg-focus-soft text-focus",
    progress: "#c44d4d",
    progressSoft: "#f6e5e2",
    label: "FOCUS"
  },
  shortBreak: {
    bg: "bg-short",
    bgHover: "hover:bg-short-hover",
    text: "text-short",
    chipActive: "bg-short-soft text-short",
    progress: "#3d8b76",
    progressSoft: "#e0ece6",
    label: "SHORT BREAK"
  },
  longBreak: {
    bg: "bg-long",
    bgHover: "hover:bg-long-hover",
    text: "text-long",
    chipActive: "bg-long-soft text-long",
    progress: "#5b7c99",
    progressSoft: "#e1e8ef",
    label: "LONG BREAK"
  }
};

function format(seconds: number): string {
  const m = Math.max(0, Math.floor(seconds / 60));
  const s = Math.max(0, seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function durationOf(mode: Mode, settings: Settings): number {
  switch (mode) {
    case "focus":
      return settings.focus * 60;
    case "shortBreak":
      return settings.shortBreak * 60;
    case "longBreak":
      return settings.longBreak * 60;
  }
}

export default function PomodoroTimer({
  settings,
  setStats,
  activeTodo,
  bumpActiveTodoPomodoro,
  completedInRound,
  setCompletedInRound,
  onRequestPickTask
}: PomodoroTimerProps) {
  const [mode, setMode] = useState<Mode>("focus");
  const [remaining, setRemaining] = useState<number>(durationOf("focus", settings));
  const [running, setRunning] = useState(false);
  const timerRef = useRef<number | null>(null);

  // 切模式或改设置 → 重置
  useEffect(() => {
    setRemaining(durationOf(mode, settings));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.focus, settings.shortBreak, settings.longBreak]);

  useEffect(() => {
    setRemaining(durationOf(mode, settings));
    setRunning(false);
  }, [mode, settings]);

  // 倒计时
  useEffect(() => {
    if (!running) return;
    timerRef.current = window.setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          window.clearInterval(timerRef.current!);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [running]);

  useEffect(() => {
    if (remaining === 0) handleComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining === 0]);

  useEffect(() => {
    document.title = running
      ? `${format(remaining)} · ${MODE_LABEL[mode]} · 番茄钟`
      : "番茄钟 · Pomodoro";
  }, [remaining, mode, running]);

  // ===== 专注模式 + 必须选任务 =====
  const needTask = mode === "focus" && !activeTodo;

  function handleStart() {
    if (needTask) {
      onRequestPickTask?.();
      return;
    }
    setRunning((r) => !r);
  }

  function handleComplete() {
    setRunning(false);
    playBeep();

    if (mode === "focus") {
      const minutes = settings.focus;
      const key = todayKey();
      setStats((prev) => {
        const today = prev.byDate[key] ?? { pomodoros: 0, minutes: 0 };
        return {
          ...prev,
          totalPomodoros: prev.totalPomodoros + 1,
          totalMinutes: prev.totalMinutes + minutes,
          byDate: {
            ...prev.byDate,
            [key]: {
              pomodoros: today.pomodoros + 1,
              minutes: today.minutes + minutes
            }
          }
        };
      });
      bumpActiveTodoPomodoro();

      const nextCompleted = completedInRound + 1;
      setCompletedInRound(nextCompleted);
      const nextMode: Mode =
        nextCompleted % settings.longBreakInterval === 0 ? "longBreak" : "shortBreak";
      setMode(nextMode);
      if (settings.autoStart) setRunning(true);
    } else {
      setMode("focus");
      if (settings.autoStart) setRunning(true);
    }
  }

  function handleReset() {
    setRunning(false);
    setRemaining(durationOf(mode, settings));
  }

  function handleSkip() {
    setRemaining(0);
  }

  const total = durationOf(mode, settings);
  const progress = total === 0 ? 0 : 1 - remaining / total;
  const theme = MODE_THEME[mode];

  const r = 120;
  const circumference = 2 * Math.PI * r;
  const offset = useMemo(() => circumference * (1 - progress), [progress, circumference]);

  return (
    <div className="flex flex-col items-center gap-6">
      {/* 模式切换 */}
      <div className="inline-flex bg-chip p-1 rounded-lg border border-hairline">
        {(Object.keys(MODE_LABEL) as Mode[]).map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`px-5 py-1.5 text-sm rounded-md font-medium ${
              mode === m ? MODE_THEME[m].chipActive + " shadow-sm" : "text-muted hover:text-ink"
            }`}
          >
            {MODE_LABEL[m]}
          </button>
        ))}
      </div>

      {/* 倒计时卡片 */}
      <div className="relative w-full max-w-md bg-paper border border-hairline rounded-2xl shadow-card py-10 px-6 flex flex-col items-center">
        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-semibold tracking-[0.2em] px-3 py-1 rounded-full border border-hairline bg-paper">
          <span className={theme.text}>{theme.label}</span>
        </div>

        <div className="relative w-72 h-72 sm:w-80 sm:h-80 flex items-center justify-center">
          <svg className="absolute inset-0 -rotate-90" viewBox="0 0 256 256">
            <circle cx="128" cy="128" r={r} fill="none" stroke={theme.progressSoft} strokeWidth="10" />
            <circle
              cx="128"
              cy="128"
              r={r}
              fill="none"
              stroke={theme.progress}
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              style={{ transition: "stroke-dashoffset 0.5s ease" }}
            />
          </svg>
          <div className="flex flex-col items-center text-center">
            <span className="font-numeric text-7xl sm:text-8xl text-ink leading-none">
              {format(remaining)}
            </span>
            <span className="mt-3 text-xs text-soft tracking-widest">
              第 {completedInRound + (mode === "focus" ? 1 : 0)} 个番茄
            </span>
          </div>
        </div>

        {/* 任务状态条（在卡片内） */}
        <div className="mt-6 mb-2 min-h-[28px] flex items-center justify-center">
          {mode === "focus" ? (
            activeTodo ? (
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${theme.chipActive} border-hairline`}>
                <span className="relative flex w-2 h-2">
                  <span className={`absolute inset-0 rounded-full ${theme.text === "text-focus" ? "bg-focus" : "bg-current"} opacity-60 animate-ping`} />
                  <span className={`relative w-2 h-2 rounded-full ${theme.text === "text-focus" ? "bg-focus" : "bg-current"}`} />
                </span>
                <span className="text-sm font-medium">{activeTodo.title}</span>
              </div>
            ) : (
              <button
                onClick={onRequestPickTask}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber bg-amber-soft text-amber text-sm hover:bg-amber hover:text-white"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>请先选择一个任务</span>
                <span className="opacity-60">↓</span>
              </button>
            )
          ) : (
            <span className="text-xs text-soft">放松一下，离开屏幕一会儿</span>
          )}
        </div>

        {/* 控制按钮 */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleStart}
            disabled={needTask && !running}
            className={`${theme.bg} ${
              needTask ? "opacity-50 cursor-not-allowed" : theme.bgHover + " shadow-insetBtn hover:shadow-cardHover active:translate-y-px"
            } text-white text-base font-semibold uppercase tracking-[0.18em] px-14 py-3 rounded-lg shadow-card`}
            title={needTask ? "请先选择一个任务" : ""}
          >
            {needTask ? "未选任务" : running ? "暂停" : "开始"}
          </button>
          <button
            onClick={handleReset}
            className="border border-line bg-paper text-body px-4 py-3 rounded-lg text-sm hover:bg-chip hover:text-ink"
          >
            重置
          </button>
          <button
            onClick={handleSkip}
            className="border border-line bg-paper text-body px-4 py-3 rounded-lg text-sm hover:bg-chip hover:text-ink"
          >
            跳过
          </button>
        </div>
      </div>
    </div>
  );
}

function playBeep() {
  try {
    const Ctx =
      (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
        .AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [
      { freq: 880, start: 0, dur: 0.15 },
      { freq: 660, start: 0.18, dur: 0.2 }
    ].forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + start);
      gain.gain.linearRampToValueAtTime(0.15, now + start + 0.02);
      gain.gain.linearRampToValueAtTime(0, now + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    });
    setTimeout(() => ctx.close(), 800);
  } catch {
    // ignore
  }
}
